import { DefaultQueryParams } from '@frontend/api-utils';
import { ChartClient } from '@frontend/chart/api';
import { EquipmentTimersDurationByProductListChart, splitRankedChartSeries } from '@frontend/chart/components';
import { Chart } from '@frontend/chart/types';
import { useEffect, useState } from 'react';

import { useInsightFetchRange } from '../../../analytics/insights/insight-detail/use-insight-fetch-range';
import { getLast30DaysChartRange } from '../../pudo/last-30-days-chart-range';
import { Last30DaysLendingAverageUseDurationByProductListCardProps } from './last-30-days-lending-average-use-duration-by-product-list-card.component';

const PRODUCT_LIMIT = 5;

export interface ProductAverageDurationItem {
    key: string;
    name: string;
    /** Average completed equipment-timer duration in seconds. */
    averageSeconds: number;
}

const toAverageDurationItems = (chart: Chart, unknownLabel: string): ProductAverageDurationItem[] =>
    chart.series.flatMap((series) => {
        const rawName = series.name?.trim();
        if (!rawName || rawName.toLowerCase() === 'default') {
            return [];
        }

        const averageSeconds = series.data.reduce((sum, point) => sum + point.value, 0);
        if (averageSeconds <= 0) {
            return [];
        }

        return [
            {
                key: rawName,
                name: rawName.toLowerCase() === 'none' ? unknownLabel : rawName,
                averageSeconds
            }
        ];
    });

export const useLast30DaysLendingAverageUseDurationByProductListCard = (
    props: Last30DaysLendingAverageUseDurationByProductListCardProps & { unknownLabel: string }
) => {
    const [highestItems, setHighestItems] = useState<ProductAverageDurationItem[]>([]);
    const [lowestItems, setLowestItems] = useState<ProductAverageDurationItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchRange = useInsightFetchRange(getLast30DaysChartRange);

    useEffect(() => {
        if (!props.accountId) {
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        setIsLoading(true);

        const { after, before } = fetchRange;

        ChartClient.fetchChartData(props.accountId, EquipmentTimersDurationByProductListChart.definition, {
            [DefaultQueryParams.AFTER]: after,
            [DefaultQueryParams.BEFORE]: before,
            ...fetchRange.entityParams
        })
            .then((chart) => {
                if (cancelled) {
                    return;
                }

                const { highest, lowest } = splitRankedChartSeries(chart, PRODUCT_LIMIT);
                setHighestItems(toAverageDurationItems(highest, props.unknownLabel));
                setLowestItems(toAverageDurationItems(lowest, props.unknownLabel));
            })
            .catch(() => {
                if (!cancelled) {
                    setHighestItems([]);
                    setLowestItems([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [props.accountId, props.unknownLabel]);

    return { highestItems, lowestItems, isLoading };
};
